import { useEffect, useMemo, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { H3HexagonLayer, TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer, ScatterplotLayer } from '@deck.gl/layers';
import { cityViews, seedHeat, type CityKey, type PeriodKey } from './lib/seedHeat';
import { loadEvents, loadHeatCells, type LiveEvent } from './lib/supabase';

type VisualHeatPoint = {
  id: string;
  h3Index?: string;
  label: string;
  category: string;
  position: [number, number];
  probability: number;
  confidence: number;
  expectedPeople: number;
  observedPeople: number;
  source: 'baseline' | 'supabase';
};

type ViewMode = 'heat' | '3d';

type HoveredItem =
  | { kind: 'heat'; point: VisualHeatPoint }
  | { kind: 'event'; event: LiveEvent };

const periods: Array<{ key: PeriodKey; label: string }> = [
  { key: 'agora', label: 'Agora' },
  { key: 'hoje', label: 'Hoje' },
  { key: 'amanha', label: 'Amanhã' },
  { key: 'fim-de-semana', label: 'Fim de semana' },
];

function probabilityLabel(value: number) {
  if (value >= 0.85) return 'muito quente';
  if (value >= 0.7) return 'quente';
  if (value >= 0.5) return 'movimento médio';
  return 'mais tranquilo';
}

function heatColor(probability: number, confidence: number): [number, number, number, number] {
  const alpha = Math.round(95 + confidence * 150);
  if (probability >= 0.85) return [176, 35, 54, alpha];
  if (probability >= 0.7) return [226, 75, 52, alpha];
  if (probability >= 0.55) return [246, 154, 62, alpha];
  if (probability >= 0.4) return [239, 218, 81, alpha];
  return [64, 166, 97, alpha];
}

function formatEventDate(value: string) {
  const date = new Date(value);
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatPrice(event: LiveEvent) {
  if (event.isFree) return 'Grátis';
  if (event.priceMin == null && event.priceMax == null) return 'Preço a confirmar';
  if (event.priceMin != null && event.priceMax != null && event.priceMin !== event.priceMax) {
    return `R$ ${event.priceMin.toLocaleString('pt-BR')}–${event.priceMax.toLocaleString('pt-BR')}`;
  }
  const value = event.priceMin ?? event.priceMax ?? 0;
  return `R$ ${value.toLocaleString('pt-BR')}`;
}

function App() {
  const [city, setCity] = useState<CityKey>('sao-paulo');
  const [period, setPeriod] = useState<PeriodKey>('agora');
  const [viewMode, setViewMode] = useState<ViewMode>('3d');
  const [liveCells, setLiveCells] = useState<VisualHeatPoint[]>([]);
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [hovered, setHovered] = useState<HoveredItem | null>(null);
  const [loading, setLoading] = useState(false);

  const cityView = cityViews[city];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([loadHeatCells(city, period), loadEvents(city, period)])
      .then(([rows, nextEvents]) => {
        if (cancelled) return;
        setLiveCells(rows.map((row) => ({
          id: row.id,
          h3Index: row.h3Index,
          label: 'Área prevista',
          category: 'previsão agregada',
          position: row.position,
          probability: row.probability,
          confidence: row.confidence,
          expectedPeople: Math.round(row.predictedDensity),
          observedPeople: Math.round(row.observedDensity),
          source: 'supabase',
        })));
        setEvents(nextEvents);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [city, period]);

  const baseline = useMemo<VisualHeatPoint[]>(() => seedHeat
    .filter((point) => point.city === city)
    .map((point) => ({
      id: point.id,
      label: point.label,
      category: point.category,
      position: point.position,
      probability: point.scores[period],
      confidence: point.confidence,
      expectedPeople: point.expectedPeople,
      observedPeople: 0,
      source: 'baseline',
    })), [city, period]);

  const points = liveCells.length > 0 ? liveCells : baseline;
  const usingBaseline = liveCells.length === 0;

  const summary = useMemo(() => {
    if (!points.length) return { probability: 0, confidence: 0, people: 0, observed: 0 };
    const weight = points.reduce((acc, point) => acc + Math.max(point.expectedPeople, 1), 0);
    const weighted = (field: 'probability' | 'confidence') => points.reduce(
      (acc, point) => acc + point[field] * Math.max(point.expectedPeople, 1),
      0,
    ) / weight;

    return {
      probability: weighted('probability'),
      confidence: weighted('confidence'),
      people: points.reduce((acc, point) => acc + point.expectedPeople, 0),
      observed: points.reduce((acc, point) => acc + point.observedPeople, 0),
    };
  }, [points]);

  const topEvents = useMemo(() => [...events]
    .sort((a, b) => {
      const heat = b.heatScore - a.heatScore;
      if (heat !== 0) return heat;
      const probability = b.probability - a.probability;
      if (probability !== 0) return probability;
      return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
    })
    .slice(0, 7), [events]);

  const tileLayer = new TileLayer({
    id: 'osm-basemap',
    data: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,
    renderSubLayers: (props: any) => {
      const { west, south, east, north } = props.tile.bbox;
      return new BitmapLayer(props, {
        data: undefined,
        image: props.data,
        bounds: [west, south, east, north],
        opacity: viewMode === '3d' ? 0.32 : 0.46,
      });
    },
  });

  const heatLayer = new HeatmapLayer<VisualHeatPoint>({
    id: `oqf-heat-${city}-${period}`,
    data: points,
    visible: viewMode === 'heat' || usingBaseline,
    getPosition: (point) => point.position,
    getWeight: (point) => Math.max(point.expectedPeople, 120)
      * point.probability
      * (0.35 + point.confidence * 0.65),
    radiusPixels: 68,
    intensity: 1.3,
    threshold: 0.03,
    colorRange: [
      [31, 122, 75],
      [99, 190, 95],
      [239, 218, 81],
      [246, 154, 62],
      [226, 75, 52],
      [167, 37, 52],
    ],
  });

  const h3Points = liveCells.filter((point): point is VisualHeatPoint & { h3Index: string } => Boolean(point.h3Index));
  const h3Layer = new H3HexagonLayer<VisualHeatPoint & { h3Index: string }>({
    id: `oqf-h3-${city}-${period}`,
    data: h3Points,
    visible: viewMode === '3d' && h3Points.length > 0,
    pickable: true,
    extruded: true,
    wireframe: false,
    getHexagon: (point) => point.h3Index,
    getFillColor: (point) => heatColor(point.probability, point.confidence),
    getElevation: (point) => 45 + Math.sqrt(Math.max(point.expectedPeople, 0)) * 16 * (0.5 + point.probability),
    elevationScale: 1,
    coverage: 0.92,
    opacity: 0.9,
    onHover: ({ object }) => setHovered(object ? { kind: 'heat', point: object } : null),
  });

  const heatPointLayer = new ScatterplotLayer<VisualHeatPoint>({
    id: `oqf-points-${city}-${period}`,
    data: points,
    visible: viewMode === 'heat' || usingBaseline,
    pickable: true,
    getPosition: (point) => point.position,
    getRadius: (point) => 8 + point.probability * 11,
    radiusUnits: 'pixels',
    getFillColor: (point) => [255, 255, 255, Math.round(70 + point.confidence * 115)],
    getLineColor: [255, 255, 255, 210],
    lineWidthUnits: 'pixels',
    getLineWidth: 1.4,
    stroked: true,
    onHover: ({ object }) => setHovered(object ? { kind: 'heat', point: object } : null),
  });

  const eventPoints = events.filter((event): event is LiveEvent & { position: [number, number] } => Boolean(event.position));
  const eventLayer = new ScatterplotLayer<LiveEvent & { position: [number, number] }>({
    id: `oqf-events-${city}-${period}`,
    data: eventPoints,
    pickable: true,
    getPosition: (event) => event.position,
    getRadius: (event) => 7 + Math.max(event.probability, 0.25) * 8,
    radiusUnits: 'pixels',
    getFillColor: (event) => heatColor(Math.max(event.probability, 0.45), Math.max(event.confidence, 0.35)),
    getLineColor: [255, 255, 255, 245],
    getLineWidth: 2,
    lineWidthUnits: 'pixels',
    stroked: true,
    onHover: ({ object }) => setHovered(object ? { kind: 'event', event: object } : null),
  });

  return (
    <main className="app-shell">
      <DeckGL
        key={`${city}-${viewMode}`}
        initialViewState={{
          longitude: cityView.longitude,
          latitude: cityView.latitude,
          zoom: cityView.zoom,
          pitch: viewMode === '3d' ? 58 : 48,
          bearing: -18,
          minZoom: 8,
          maxZoom: 18,
        }}
        controller
        layers={[tileLayer, heatLayer, h3Layer, heatPointLayer, eventLayer]}
      />

      <header className="topbar glass">
        <div className="brand-block">
          <span className="brand-mark">OQF</span>
          <div>
            <strong>OQFazer</strong>
            <small>oqfazer.com · a cidade em tempo real</small>
          </div>
        </div>

        <div className="topbar-actions">
          <div className="view-switcher" role="group" aria-label="Visualização">
            <button className={viewMode === 'heat' ? 'active' : ''} onClick={() => setViewMode('heat')}>Calor</button>
            <button className={viewMode === '3d' ? 'active' : ''} onClick={() => setViewMode('3d')}>3D</button>
          </div>
          <div className="city-switcher" role="group" aria-label="Cidade">
            {(Object.keys(cityViews) as CityKey[]).map((key) => (
              <button
                key={key}
                className={key === city ? 'active' : ''}
                onClick={() => setCity(key)}
              >
                {cityViews[key].name}
              </button>
            ))}
          </div>
        </div>
      </header>

      <section className="hero-copy glass">
        <p className="eyebrow">MAPA PROBABILÍSTICO · CONFIANÇA EXPLÍCITA</p>
        <h1>{cityView.name} está <span>{probabilityLabel(summary.probability)}</span></h1>
        <p>
          Mesmo com zero check-ins, o mapa nasce preenchido por um baseline estatístico. Eventos reais,
          histórico e sinais de presença elevam ou reduzem a previsão e a confiabilidade de cada área.
        </p>

        <div className="period-tabs" role="group" aria-label="Período">
          {periods.map((item) => (
            <button
              key={item.key}
              className={period === item.key ? 'active' : ''}
              onClick={() => setPeriod(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>

      <aside className="metrics glass">
        <div>
          <span>Probabilidade média</span>
          <strong>{Math.round(summary.probability * 100)}%</strong>
        </div>
        <div>
          <span>Confiabilidade</span>
          <strong>{Math.round(summary.confidence * 100)}%</strong>
        </div>
        <div>
          <span>Movimento estimado</span>
          <strong>{summary.people.toLocaleString('pt-BR')}</strong>
        </div>
        <div>
          <span>Eventos no período</span>
          <strong>{events.length}</strong>
        </div>
        <div className="source-row">
          <span className={`status-dot ${loading ? 'loading' : ''}`} />
          {loading ? 'atualizando previsão' : usingBaseline ? 'baseline estatístico ativo' : 'previsão Supabase + H3 ativa'}
        </div>
      </aside>

      <aside className="events-panel glass">
        <div className="events-heading">
          <div>
            <p className="eyebrow">O QUE FAZER</p>
            <strong>{events.length ? 'Eventos encontrados' : 'Radar iniciando'}</strong>
          </div>
          <span>{events.length}</span>
        </div>

        <div className="event-list">
          {topEvents.length > 0 ? topEvents.map((event) => (
            <button
              type="button"
              className="event-card"
              key={event.id}
              onMouseEnter={() => setHovered({ kind: 'event', event })}
              onMouseLeave={() => setHovered(null)}
              onClick={() => {
                const url = event.ticketUrl ?? event.officialUrl;
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              <div className="event-card-top">
                <span>{event.category}</span>
                <b>{Math.round(event.probability * 100)}%</b>
              </div>
              <strong>{event.title}</strong>
              <small>{formatEventDate(event.startsAt)} · {event.neighborhood ?? event.venueName ?? cityView.name}</small>
              <div className="event-card-bottom">
                <span>{formatPrice(event)}</span>
                <span>conf. {Math.round(event.confidence * 100)}%</span>
              </div>
            </button>
          )) : (
            <div className="empty-events">
              <strong>O mapa já funciona sem contribuições.</strong>
              <span>O crawler começa a preencher os eventos reais nesta camada.</span>
            </div>
          )}
        </div>
      </aside>

      {hovered?.kind === 'heat' && (
        <div className="map-tooltip glass">
          <strong>{hovered.point.label}</strong>
          <span>{hovered.point.category}</span>
          <div className="tooltip-grid">
            <span>chance <b>{Math.round(hovered.point.probability * 100)}%</b></span>
            <span>confiança <b>{Math.round(hovered.point.confidence * 100)}%</b></span>
            <span>estimado <b>{hovered.point.expectedPeople.toLocaleString('pt-BR')}</b></span>
            <span>observado <b>{hovered.point.observedPeople.toLocaleString('pt-BR')}</b></span>
          </div>
        </div>
      )}

      {hovered?.kind === 'event' && (
        <div className="map-tooltip event-tooltip glass">
          <strong>{hovered.event.title}</strong>
          <span>{formatEventDate(hovered.event.startsAt)}</span>
          <span>{hovered.event.venueName ?? hovered.event.address ?? cityView.name}</span>
          <div className="tooltip-grid">
            <span>chance <b>{Math.round(hovered.event.probability * 100)}%</b></span>
            <span>confiança <b>{Math.round(hovered.event.confidence * 100)}%</b></span>
          </div>
        </div>
      )}

      <div className="legend glass" aria-label="Legenda do mapa">
        <span>tranquilo</span>
        <div className="heat-gradient" />
        <span>muito quente</span>
      </div>

      <footer className="map-credit">Mapa © OpenStreetMap contributors · OQFazer alpha</footer>
    </main>
  );
}

export default App;
